/**
 * "Balanca e pushimeve" — company-wide leave balance per worker, for one leave
 * type at a time (annual by default; the selector lists every `leave_types` row).
 *
 * Every number on this screen comes from GET /api/reports/leave-balance and is
 * computed by backend/services/leaveSummary.js. This file does NOT derive any
 * balance figure: the same service also feeds "Pushimet e mia", so the two
 * screens can never disagree about the same worker. The previous version pulled
 * /api/workers + /api/holidays (both plain requireAuth) and did the arithmetic
 * in the browser — that both duplicated the logic and let any authenticated
 * employee read the whole company's leave data.
 *
 * Sorting, searching, paging and column visibility are presentation and stay
 * here; filters that change WHICH rows exist are query parameters.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody,
  Button, LinearProgress, Stack, Typography, Chip, TableContainer,
  TextField, InputAdornment, MenuItem, Select, FormControl, InputLabel,
  TableSortLabel, TablePagination, IconButton, Menu, Checkbox, FormControlLabel,
  Tooltip, Alert, AlertTitle, Divider, Skeleton, ListItemIcon, ListItemText,
} from '@mui/material';
import DownloadIcon        from '@mui/icons-material/Download';
import EmailIcon           from '@mui/icons-material/Email';
import SearchIcon          from '@mui/icons-material/Search';
import ViewColumnIcon      from '@mui/icons-material/ViewColumn';
import RefreshRoundedIcon  from '@mui/icons-material/RefreshRounded';
import WarningAmberIcon    from '@mui/icons-material/WarningAmber';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import { useTranslation }  from 'react-i18next';
import { printReport, textCol, numCol } from '../../utils/pdfPrint';
import { exportLeaveBalanceExcel } from '../../utils/leaveBalanceExcel';
import { buildLeaveMatrix, exportLeaveMatrixExcel } from '../../utils/leaveMatrix';
import WorkerPicker from '../common/WorkerPicker';
import apiService, { authFetch } from '../../services/apiServices';
import PageHeader from '../common/PageHeader';
import SendReportEmailDialog from '../common/SendReportEmailDialog';
import { ReportPage, ExportMenu } from './ReportKit';

/* ─── Column model ────────────────────────────────────────────────────────────
 * `calc` marks a column whose value the backend derives; the formula lives in
 * docs/notes/leave_balance_report.md and in leaveSummary.js beside the code. */
export const COLUMNS = [
  { id: 'workerName',      label: 'Punonjësi',        type: 'text', always: true },
  { id: 'department',      label: 'Departamenti',     type: 'text' },
  { id: 'position',        label: 'Pozita',           type: 'text' },
  { id: 'hireDate',        label: 'Data e fillimit',  type: 'date' },
  { id: 'period',          label: 'Periudha',         type: 'text' },
  { id: 'baseAnnualDays',  label: 'Bazë vjetore',     type: 'days' },
  { id: 'proRataDays',     label: 'Proporcionale',    type: 'days', calc: true },
  { id: 'carriedOverDays', label: 'Bartur',           type: 'days' },
  { id: 'extraDays',       label: 'Shtesë',           type: 'days', calc: true },
  { id: 'adjustmentDays',  label: 'Korrigjim ±',      type: 'days', signed: true },
  { id: 'approvedDays',    label: 'Aprovuar',         type: 'days', calc: true },
  { id: 'usedDays',        label: 'Përdorur',         type: 'days', calc: true },
  { id: 'pendingDays',     label: 'Në pritje',        type: 'days', calc: true },
  { id: 'remainingDays',   label: 'Mbetur',           type: 'days', calc: true, emphasis: true },
  { id: 'lastUpdatedAt',   label: 'Përditësuar',      type: 'datetime' },
];

/** The columns "Të gjitha" actually governs — `always` ones cannot be hidden. */
export const TOGGLEABLE_COLUMN_IDS = COLUMNS.filter((c) => !c.always).map((c) => c.id);

/** Shown by default — the rest stay a click away so the table starts readable. */
const DEFAULT_HIDDEN = new Set(['position', 'hireDate', 'baseAnnualDays', 'lastUpdatedAt']);

const LS_KEY = 'nexuma_leavebalance_cols';
const LS_TYPES_KEY = 'nexuma_leavebalance_types';

const fmtDays = (n) => {
  const r = Math.round((Number(n) || 0) * 100) / 100;
  return r.toFixed(2).replace(/\.?0+$/, '');
};
const fmtSigned = (n) => ((Number(n) || 0) > 0 ? `+${fmtDays(n)}` : fmtDays(n));
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('sq-AL', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('sq-AL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

/** The value a cell renders, per column type. */
export function cellValue(row, col) {
  if (col.id === 'period') return `${row.year}`;
  const v = row[col.id];
  // A type with no yearly cap (sick on a certificate, unpaid) has nothing to
  // run out of — "—", not a fake 0 or a negative.
  if (col.type === 'days' && v === null) return '—';
  if (col.type === 'days')     return col.signed ? fmtSigned(v) : fmtDays(v);
  if (col.type === 'date')     return fmtDate(v);
  if (col.type === 'datetime') return fmtDateTime(v);
  return v || '—';
}

/** Sort key — numeric columns must not sort as strings. */
export function sortValue(row, colId) {
  const col = COLUMNS.find((c) => c.id === colId);
  if (col?.type === 'days') return Number(row[colId]) || 0;
  if (col?.id === 'period') return Number(row.year) || 0;
  if (col?.type === 'date' || col?.type === 'datetime') return row[colId] ? new Date(row[colId]).getTime() : 0;
  return String(row[colId] ?? '').toLocaleLowerCase('sq');
}

const YEARS = (() => {
  const y = new Date().getFullYear();
  return [y + 1, y, y - 1, y - 2, y - 3];
})();

/* ─── Chrome ──────────────────────────────────────────────────────────────────
 * One palette for the whole screen. The report is read as a grid of numbers, so
 * the frame stays quiet: hairline rules, one accent, colour only where it means
 * something (a negative balance, a mismatch). */
const C = {
  ink: '#0F172A', ink700: '#334155', ink500: '#64748B', ink400: '#94A3B8',
  line: '#E5E7EB', hair: '#F1F5F9', surface: '#F8FAFC', white: '#FFFFFF',
  // Theksi është MARKA. Ishte indigo `#4338CA` — një ngjyrë e dytë e shpikur
  // për këtë faqe të vetme, kurse rregulli i ReportKit-it është një theks plus
  // neutrale, dhe ngjyrat semantike vetëm për status.
  accent: '#3E8FA3', good: '#15803D', bad: '#B91C1C', warn: '#B45309',
};

/** A figure in the summary strip — label above, number below, nothing else. */
function Stat({ label, value, tone, loading }) {
  return (
    // Hapësira rri KËTU, jo te kontejneri: `Stack`-i i jashtëm vendos ndarëset
    // vertikale mes fëmijëve, ndaj një `gap` do t'i largonte ato nga teksti.
    // Pa këto vlera etiketat ngjiten njëra me tjetrën ("PUNONJËSTË DREJTAT…")
    // dhe ndarëset zhduken pas tyre.
    <Box sx={{ px: 1.75, py: 0.875, minWidth: 84 }}>
      <Typography sx={{
        fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.06em',
        textTransform: 'uppercase', color: C.ink400, lineHeight: 1.5, whiteSpace: 'nowrap',
      }}>
        {label}
      </Typography>
      {loading ? <Skeleton width={44} height={20} /> : (
        <Typography sx={{
          fontSize: '1rem', fontWeight: 800, lineHeight: 1.2,
          fontVariantNumeric: 'tabular-nums', color: tone || C.ink,
        }}>
          {value}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Chrome-i i menusë së kolonave.
 *
 * `outlineOffset: -2px` — e njëjta zgjidhje si `navFocusRing` te
 * SidebarRenderer. Unaza e fokusit e temës rri 2px JASHTË elementit; mbi një
 * rresht menuje ajo lexohet si kornizë inputi, dhe meqë `<Menu>` e fokuson
 * vetvetiu zërin e parë, çdo hapje e menusë e vizatonte atë kuti blu rreth
 * «Të gjitha». E tërhequr brenda, mbetet tregues fokusi dhe s'është më kuti.
 *
 * Rreshtat janë të ngjeshur (28px) sepse janë katërmbëdhjetë: me lartësinë e
 * parazgjedhur menuja del më e gjatë se vetë tabela.
 */
const MENU_SX = {
  py: 0.5,
  '& .MuiMenuItem-root': {
    minHeight: 28,
    py: 0.125,
    px: 1.25,
    borderRadius: 1,
    mx: 0.5,
    '&:focus-visible': { outlineOffset: '-2px' },
  },
  '& .MuiCheckbox-root': { p: 0.5, mr: 0.25 },
};

/** Dense filter select — same height as everything else on the toolbar. */
const selectSx = {
  minWidth: 0,
  '& .MuiOutlinedInput-root': { height: 34, borderRadius: '8px', bgcolor: C.white, fontSize: '0.8rem' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: C.line },
  '& .MuiInputLabel-root': { fontSize: '0.8rem' },
};

export default function LeaveBalanceReport() {
  const { t } = useTranslation();

  const [year, setYear]           = useState(new Date().getFullYear());
  const [departmentId, setDept]   = useState('');
  const [workerId, setWorkerId]   = useState('');
  const [status, setStatus]       = useState('active');
  // Llojet e zgjedhura me kutiza. `null` = të gjitha (parazgjedhja, edhe kur
  // kompania shton një lloj të ri). Një lloj i vetëm hap pamjen e detajuar të
  // balancës; disa lloje — tabelën me një kolonë për secilin.
  const [selectedTypes, setSelectedTypes] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(LS_TYPES_KEY) || 'null');
      return Array.isArray(raw) ? raw.map(Number) : null;
    } catch { return null; }
  });
  useEffect(() => {
    try { localStorage.setItem(LS_TYPES_KEY, JSON.stringify(selectedTypes)); } catch { /* non-fatal */ }
  }, [selectedTypes]);
  const leaveTypeId = Array.isArray(selectedTypes) && selectedTypes.length === 1 ? selectedTypes[0] : 'all';
  const [leaveTypes, setLeaveTypes]   = useState([]);
  const [search, setSearch]       = useState('');

  const [rows, setRows]           = useState([]);
  const [totals, setTotals]       = useState(null);
  const [departments, setDepts]   = useState([]);
  const [workers, setWorkers]     = useState([]);
  const [company, setCompany]     = useState('');
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [emailOpen, setEmailOpen] = useState(false);

  const [orderBy, setOrderBy]     = useState('workerName');
  const [order, setOrder]         = useState('asc');
  const [page, setPage]           = useState(0);
  const [rpp, setRpp]             = useState(25);
  const [colAnchor, setColAnchor] = useState(null);
  const [exportAnchor, setExportAnchor] = useState(null);

  const [hidden, setHidden] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
      if (Array.isArray(raw)) return new Set(raw);
    } catch { /* fall through to the default */ }
    return new Set(DEFAULT_HIDDEN);
  });
  useEffect(() => {
    try { localStorage.setItem(LS_KEY, JSON.stringify([...hidden])); } catch { /* non-fatal */ }
  }, [hidden]);

  const visibleColumns = useMemo(() => COLUMNS.filter((c) => c.always || !hidden.has(c.id)), [hidden]);

  /* Select-all state. Measured over the TOGGLEABLE columns only — `always`
   * columns are never hidden, so counting them would peg the box at "all". */
  const allColumnsShown  = useMemo(() => TOGGLEABLE_COLUMN_IDS.every((id) => !hidden.has(id)), [hidden]);
  const someColumnsShown = useMemo(() => TOGGLEABLE_COLUMN_IDS.some((id) => !hidden.has(id)), [hidden]);

  /* ── filter option lists + the company name that heads the exports ── */
  useEffect(() => {
    Promise.all([
      apiService().get('departments').catch(() => null),
      apiService().get('selectworker').catch(() => null),
      apiService().get('settings').catch(() => null),
    ]).then(([d, w, s]) => {
      setDepts((d?.data || []).filter((x) => x?.Name || x?.name));
      setWorkers(w?.data || []);
      setCompany(s?.data?.[0]?.CompanyName || '');
    });
  }, []);

  /* ── data ── */
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const qs = new URLSearchParams({ year: String(year), status });
      if (departmentId) qs.set('departmentId', String(departmentId));
      if (workerId)     qs.set('workerId', String(workerId));
      if (leaveTypeId === 'all') qs.set('include', 'types');
      else if (leaveTypeId) qs.set('leaveTypeId', String(leaveTypeId));

      const res  = await authFetch(`/api/reports/leave-balance?${qs.toString()}`);
      const json = await res.json().catch(() => ({}));

      if (res.status === 403) {
        setError('Nuk keni leje për të parë balancën e pushimeve të kompanisë.');
        setRows([]); setTotals(null);
        return;
      }
      if (!res.ok || json.success === false) {
        setError(json.error || `Nuk u morën të dhënat (HTTP ${res.status}).`);
        setRows([]); setTotals(null);
        return;
      }
      setRows(json.data?.rows || []);
      setTotals(json.data?.totals || null);
      setLeaveTypes(json.data?.leaveTypes || []);
      setPage(0);
    } catch {
      setError('Nuk u lidh dot me serverin. Kontrollo lidhjen dhe provo përsëri.');
      setRows([]); setTotals(null);
    } finally {
      setLoading(false);
    }
  }, [year, status, departmentId, workerId, leaveTypeId]);

  useEffect(() => { load(); }, [load]);

  /* ── search + sort (presentation only) ── */
  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('sq');
    if (!q) return rows;
    return rows.filter((r) =>
      `${r.workerName} ${r.department} ${r.position}`.toLocaleLowerCase('sq').includes(q));
  }, [rows, search]);

  const sorted = useMemo(() => {
    const dir = order === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = sortValue(a, orderBy);
      const bv = sortValue(b, orderBy);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [filtered, orderBy, order]);

  /* "Të gjitha llojet": të njëjtat rreshta, të rreshtuar si matricë lloj × punëtor. */
  const isAll = leaveTypeId === 'all';
  const matrix = useMemo(
    () => (isAll ? buildLeaveMatrix(filtered, leaveTypes, selectedTypes) : null),
    [isAll, filtered, leaveTypes, selectedTypes]
  );
  const noTypeChosen = Array.isArray(selectedTypes) && selectedTypes.length === 0;
  const allChosen = selectedTypes === null || (leaveTypes.length > 0 && leaveTypes.every((lt) => selectedTypes.includes(Number(lt.Id))));
  const toggleType = (id) => setSelectedTypes((prev) => {
    const cur = prev === null ? leaveTypes.map((lt) => Number(lt.Id)) : prev;
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    return leaveTypes.length && next.length === leaveTypes.length ? null : next;
  });
  const matrixSorted = useMemo(() => {
    if (!matrix) return [];
    const dir = order === 'asc' ? 1 : -1;
    const key = matrix.columns.some((c) => c.key === orderBy) ? orderBy : 'workerName';
    return [...matrix.rows].sort((a, b) => {
      const av = typeof a[key] === 'number' ? a[key] : String(a[key] ?? '').toLocaleLowerCase('sq');
      const bv = typeof b[key] === 'number' ? b[key] : String(b[key] ?? '').toLocaleLowerCase('sq');
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [matrix, orderBy, order]);

  const listForPaging = isAll ? matrixSorted : sorted;
  const paged = useMemo(
    () => listForPaging.slice(page * rpp, page * rpp + rpp),
    [listForPaging, page, rpp]
  );

  const toggleSort = (colId) => {
    if (orderBy === colId) setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else { setOrderBy(colId); setOrder('asc'); }
  };

  const typeName = useMemo(() => {
    if (allChosen) return 'Të gjitha llojet';
    const names = leaveTypes.filter((x) => (selectedTypes || []).includes(Number(x.Id))).map((x) => x.Name);
    return names.length ? names.join(', ') : 'Asnjë lloj';
  }, [leaveTypes, selectedTypes, allChosen]);

  const discrepancyCount = useMemo(() => filtered.filter((r) => r.hasDiscrepancy).length, [filtered]);

  const emailCols = isAll && matrix
    ? matrix.columns.map((c, i) => (c.type === 'days'
      ? numCol(c.label, c.key, `${Math.floor(70 / Math.max(matrix.columns.length - 2, 1))}%`)
      : textCol(c.label, c.key, i === 0 ? '18%' : '12%')))
    : [
      textCol('Punonjësi', 'workerName', '34%'),
      numCol('Të drejtat', 'entitlementDays', '22%'),
      numCol('Përdorur', 'usedDays', '22%'),
      numCol('Mbetur', 'remainingDays', '22%'),
    ];
  const exportRows = isAll ? matrixSorted : sorted;

  /* The line that heads the export: which slice of the company this file is. */
  const filterLine = useMemo(() => {
    const d = departments.find((x) => String(x.Id ?? x.id) === String(departmentId));
    const w = workers.find((x) => String(x.Id ?? x.id) === String(workerId));
    const dep = departmentId ? (d?.Name || d?.name || '—') : 'Të gjitha';
    const wrk = workerId
      ? (w ? (w.FullName || `${w.FirstName || ''} ${w.LastName || ''}`.trim()) : '—')
      : 'Të gjithë';
    const st = { active: 'Aktivë', inactive: 'Joaktivë', all: 'Të gjithë' }[status] || status;
    return `Llojet: ${typeName}  ·  Departamenti: ${dep}  ·  Punonjësi: ${wrk}  ·  Statusi: ${st}`
      + (search.trim() ? `  ·  Kërkimi: "${search.trim()}"` : '');
  }, [departmentId, workerId, status, search, departments, workers, typeName]);

  /* Excel writes every field the backend returns, not just the visible columns. */
  const exportExcel = () => {
    if (isAll && matrix) {
      exportLeaveMatrixExcel({ columns: matrix.columns, rows: matrixSorted, totals: matrix.totals, year, company, filterLine });
      return;
    }
    exportLeaveBalanceExcel({ rows: sorted, year, company, filterLine });
  };

  const exportCsv = () => {
    const cols = isAll && matrix
      ? matrix.columns.map((c) => ({ id: c.key, label: c.sub ? `${c.label} (${c.sub})` : c.label, type: c.type }))
      : visibleColumns;
    const head = cols.map((c) => c.label);
    const body = exportRows.map((r) => cols.map((c) => cellValue(r, c)));
    const csv = [head, ...body]
      .map((line) => line.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url; a.download = `balanca-pushimeve-${year}.csv`; a.click();
    URL.revokeObjectURL(url);
  };
  /* Header and body cells share one rhythm: 30px header, 30px rows, hairline
     rules. Everything below only varies alignment and weight. */
  const thSx = {
    fontWeight: 700, fontSize: '0.65rem', py: 0.75, px: 1.25, whiteSpace: 'nowrap',
    color: C.ink500, bgcolor: C.surface, letterSpacing: '0.05em',
    textTransform: 'uppercase', borderBottom: `1px solid ${C.line}`, lineHeight: 1.4,
  };
  const tdSx = {
    py: 0.5, px: 1.25, fontSize: '0.78rem', color: C.ink700,
    borderBottom: `1px solid ${C.hair}`, whiteSpace: 'nowrap',
  };
  /* The name column stays put while the twelve number columns scroll under it —
     otherwise a horizontal scroll leaves a row of figures with no owner. */
  const stickyName = {
    position: 'sticky', left: 0, zIndex: 2, bgcolor: 'inherit',
    borderRight: `1px solid ${C.hair}`,
  };

  const exportsDisabled = loading || !exportRows.length;

  return (
    <ReportPage>
      <PageHeader title={t('report.leaveBalance', 'Balanca e pushimeve')} />

      <Card elevation={0} sx={{ border: `1px solid ${C.line}`, borderRadius: 2, overflow: 'hidden' }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>

          {/* ── Toolbar: filters left, actions right, one row ── */}
          <Box sx={{
            px: 1.5, py: 1, display: 'flex', gap: 0.875, flexWrap: 'wrap',
            alignItems: 'center', borderBottom: `1px solid ${C.hair}`, bgcolor: C.white,
          }}>
            <FormControl size="small" sx={{ ...selectSx, width: 96 }}>
              <InputLabel>Viti</InputLabel>
              <Select label="Viti" value={year} onChange={(e) => setYear(Number(e.target.value))}>
                {YEARS.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ ...selectSx, width: 200 }}>
              <InputLabel>Llojet e pushimit</InputLabel>
              <Select
                multiple label="Llojet e pushimit"
                value={selectedTypes === null ? leaveTypes.map((lt) => Number(lt.Id)) : selectedTypes}
                renderValue={() => (allChosen ? 'Të gjitha llojet'
                  : noTypeChosen ? 'Asnjë lloj'
                  : (selectedTypes.length === 1 ? typeName : `${selectedTypes.length} lloje`))}
                MenuProps={{ PaperProps: { sx: { maxHeight: 440 } } }}
                onChange={() => {}}
              >
                <MenuItem dense onClick={() => { setSelectedTypes(allChosen ? [] : null); setPage(0); }}>
                  <Checkbox size="small" checked={allChosen}
                    indeterminate={!allChosen && !noTypeChosen} sx={{ p: 0.5, mr: 1 }} />
                  <ListItemText primary="Të gjitha llojet" primaryTypographyProps={{ fontWeight: 700, fontSize: '0.85rem' }} />
                </MenuItem>
                <Divider />
                {leaveTypes.map((lt) => {
                  const id = Number(lt.Id);
                  const on = selectedTypes === null || selectedTypes.includes(id);
                  return (
                    <MenuItem dense key={id} value={id} onClick={() => { toggleType(id); setPage(0); }}>
                      <Checkbox size="small" checked={on} sx={{ p: 0.5, mr: 1 }} />
                      <ListItemText
                        primary={lt.Name}
                        secondary={[lt.Rule?.label, lt.LpReference].filter(Boolean).join(' · ')}
                        primaryTypographyProps={{ fontSize: '0.85rem' }}
                        secondaryTypographyProps={{ fontSize: '0.72rem' }}
                      />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ ...selectSx, width: 156 }}>
              <InputLabel>Departamenti</InputLabel>
              <Select label="Departamenti" value={departmentId} onChange={(e) => setDept(e.target.value)}>
                <MenuItem value=""><em>Të gjitha</em></MenuItem>
                {departments.map((d) => (
                  <MenuItem key={d.Id ?? d.id} value={d.Id ?? d.id}>{d.Name || d.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Searchable — the plain select held the whole company in one list. */}
            <WorkerPicker
              workers={workers} value={workerId} emptyLabel="Të gjithë"
              onChange={(v) => { setWorkerId(v); setPage(0); }}
              fullWidth={false} accent={C.accent}
              sx={{ ...selectSx, width: 180, '& .MuiAutocomplete-inputRoot': { height: 34, py: '0 !important' } }}
            />

            <FormControl size="small" sx={{ ...selectSx, width: 116 }}>
              <InputLabel>Statusi</InputLabel>
              <Select label="Statusi" value={status} onChange={(e) => setStatus(e.target.value)}>
                <MenuItem value="active">Aktivë</MenuItem>
                <MenuItem value="inactive">Joaktivë</MenuItem>
                <MenuItem value="all">Të gjithë</MenuItem>
              </Select>
            </FormControl>

            <TextField
              size="small" placeholder="Kërko punonjës, departament, pozitë…"
              value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              sx={{
                flex: '1 1 200px', minWidth: 170,
                '& .MuiOutlinedInput-root': { height: 34, borderRadius: '8px', bgcolor: C.white, fontSize: '0.8rem' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: C.line },
              }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: C.ink400 }} /></InputAdornment> }}
            />

            {/* Actions sit at the far end of the same row — a report toolbar,
                not a strip of buttons parked under the table. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
              {!isAll && <Tooltip title="Kolonat">
                <IconButton size="small" onClick={(e) => setColAnchor(e.currentTarget)}
                  sx={{ border: `1px solid ${C.line}`, borderRadius: '8px', width: 34, height: 34 }}>
                  <ViewColumnIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>}
              {/* span wrapper: a disabled button fires no events, so MUI cannot
                  hang the tooltip off it directly. */}
              <Tooltip title="Rifresko">
                <span>
                  <IconButton size="small" onClick={load} disabled={loading}
                    sx={{ border: `1px solid ${C.line}`, borderRadius: '8px', width: 34, height: 34 }}>
                    <RefreshRoundedIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="Të gjitha kolonat, përmbledhja sipas departamentit dhe shpjegimi i formulave">
                <span>
                  <ExportMenu onExcel={exportExcel} disabled={exportsDisabled} />
                </span>
              </Tooltip>
              <Tooltip title="Eksporte të tjera">
                <IconButton size="small" onClick={(e) => setExportAnchor(e.currentTarget)}
                  sx={{ border: `1px solid ${C.line}`, borderRadius: '8px', width: 34, height: 34 }}>
                  <MoreVertRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Other exports — CSV and PDF are occasional, so they leave the surface. */}
          <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
            <MenuItem dense disabled={exportsDisabled}
              onClick={() => { setExportAnchor(null); exportCsv(); }}>
              <ListItemIcon><DownloadIcon sx={{ fontSize: 17 }} /></ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: '0.82rem' }}>Shkarko CSV</ListItemText>
            </MenuItem>
            <MenuItem dense disabled={exportsDisabled}
              onClick={() => {
                setExportAnchor(null);
                printReport({
                  title: `Balanca e pushimeve — ${typeName}`,
                  period: `Viti ${year}`,
                  columns: emailCols,
                  rows: exportRows,
                  orientation: isAll ? 'landscape' : undefined,
                  filename: `balanca-pushimeve-${year}`,
                });
              }}>
              <ListItemIcon><PictureAsPdfRoundedIcon sx={{ fontSize: 17 }} /></ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: '0.82rem' }}>Printo / PDF</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem dense disabled={exportsDisabled}
              onClick={() => { setExportAnchor(null); setEmailOpen(true); }}>
              <ListItemIcon><EmailIcon sx={{ fontSize: 17 }} /></ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: '0.82rem' }}>Dërgo me email</ListItemText>
            </MenuItem>
          </Menu>

          {/* Column visibility */}
          <Menu
            anchorEl={colAnchor} open={Boolean(colAnchor)} onClose={() => setColAnchor(null)}
            MenuListProps={{ dense: true, sx: MENU_SX }}
            slotProps={{ paper: { sx: { minWidth: 208, borderRadius: 2 } } }}
          >
            <Typography sx={{ px: 2, pt: 1, pb: 0.5, fontSize: '0.68rem', fontWeight: 700, color: C.ink400, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Shfaq kolonat
            </Typography>

            {/* Të gjitha — një klikim në vend të katërmbëdhjetë.
                `always` (Punonjësi) nuk numërohet as këtu as te toggle-i: ajo
                kolonë s'fshihet dot, ndaj po ta llogarisnim, "të gjitha" nuk do
                të dukej kurrë e plotë. Gjendja e ndërmjetme shfaqet si
                `indeterminate`, që të mos lexohet as si "të gjitha" as si
                "asnjë" — të dyja do të ishin gënjeshtra. */}
            <MenuItem onClick={() => setHidden(allColumnsShown ? new Set(TOGGLEABLE_COLUMN_IDS) : new Set())}>
              <FormControlLabel
                sx={{ pointerEvents: 'none', m: 0 }}
                control={(
                  <Checkbox
                    size="small"
                    checked={allColumnsShown}
                    indeterminate={!allColumnsShown && someColumnsShown}
                  />
                )}
                label={<Typography sx={{ fontSize: '0.8rem', fontWeight: 700 }}>Të gjitha</Typography>}
              />
            </MenuItem>
            <Divider sx={{ my: '0 !important' }} />

            {COLUMNS.map((c) => (
              <MenuItem key={c.id} disabled={c.always} onClick={() => setHidden((prev) => {
                const next = new Set(prev);
                if (next.has(c.id)) next.delete(c.id); else next.add(c.id);
                return next;
              })}>
                <FormControlLabel
                  sx={{ pointerEvents: 'none', m: 0 }}
                  control={<Checkbox size="small" checked={c.always || !hidden.has(c.id)} disabled={c.always} />}
                  label={<Typography sx={{ fontSize: '0.8rem' }}>{c.label}</Typography>}
                />
              </MenuItem>
            ))}
          </Menu>

          {/* ── Summary strip — the six figures the report exists to produce ── */}
          {!error && (
            <Stack
              direction="row" flexWrap="wrap" alignItems="center"
              divider={<Divider orientation="vertical" flexItem sx={{ my: 1.25, borderColor: C.hair }} />}
              sx={{ px: 0.5, bgcolor: C.surface, borderBottom: `1px solid ${C.hair}` }}
            >
              {isAll && matrix ? (<>
              <Stat label="Punonjës"        value={matrix.totals.workers} loading={loading} />
              <Stat label="Vjetor përdorur" value={fmtDays(matrix.totals.annualUsed)} loading={loading} />
              <Stat label="Vjetor mbetur"   value={fmtDays(matrix.totals.annualRemaining)} loading={loading}
                tone={Number(matrix.totals.annualRemaining) < 0 ? C.bad : C.good} />
              <Stat label="Në pritje"       value={fmtDays(matrix.totals.pendingAll)} loading={loading}
                tone={Number(matrix.totals.pendingAll) > 0 ? C.warn : undefined} />
              <Stat label="Gjithsej ditë pushimi" value={fmtDays(matrix.totals.totalDays)} loading={loading} />
              </>) : (<>
              <Stat label="Punonjës"   value={totals?.workers ?? 0} loading={loading} />
              <Stat label="Të drejtat" value={fmtDays(totals?.entitlementDays)} loading={loading} />
              <Stat label="Aprovuar"   value={fmtDays(totals?.approvedDays)} loading={loading} />
              <Stat label="Përdorur"   value={fmtDays(totals?.usedDays)} loading={loading} />
              <Stat label="Në pritje"  value={fmtDays(totals?.pendingDays)} loading={loading}
                tone={Number(totals?.pendingDays) > 0 ? C.warn : undefined} />
              <Stat label="Mbetur"     value={fmtDays(totals?.remainingDays)} loading={loading}
                tone={Number(totals?.remainingDays) < 0 ? C.bad : C.good} />
              </>)}

              {/* The verification result belongs beside the numbers it qualifies,
                  not in a banner above them. */}
              {!loading && discrepancyCount > 0 && (
                <Box sx={{ px: 1.75, py: 0.875, ml: 'auto' }}>
                  <Tooltip title={`Numëruesit e ruajtur te leave_balances nuk përputhen me kërkesat reale për ${discrepancyCount} ${discrepancyCount === 1 ? 'punonjës' : 'punonjës'}. Kolonat tregojnë gjithmonë vlerën e llogaritur nga kërkesat.`}>
                    <Chip
                      size="small" icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
                      label={`${discrepancyCount} mospërputhje`}
                      sx={{
                        height: 22, fontSize: '0.68rem', fontWeight: 700,
                        bgcolor: '#FEF3C7', color: C.warn,
                        '& .MuiChip-icon': { color: C.warn, ml: 0.75 },
                      }}
                    />
                  </Tooltip>
                </Box>
              )}
            </Stack>
          )}

          {loading && <LinearProgress />}

          {/* ── Error ── */}
          {!loading && error && (
            <Box sx={{ p: 2 }}>
              <Alert severity="error" sx={{ borderRadius: 2, fontSize: '0.85rem' }}
                action={<Button size="small" onClick={load} sx={{ textTransform: 'none', fontWeight: 700 }}>Provo përsëri</Button>}>
                <AlertTitle sx={{ fontSize: '0.85rem', fontWeight: 700 }}>Nuk u shfaq raporti</AlertTitle>
                {error}
              </Alert>
            </Box>
          )}

          {/* ── Të gjitha llojet: vjetori + çdo lloj tjetër i përdorur, një rresht për punëtor ── */}
          {!error && isAll && matrix && (
            <TableContainer sx={{ maxHeight: 'calc(100vh - 330px)', minHeight: 240 }}>
              <Table size="small" stickyHeader sx={{ '& td, & th': { borderColor: C.hair } }}>
                <TableHead>
                  <TableRow>
                    {matrix.columns.map((c, i) => (
                      <TableCell key={c.key} sx={{
                        ...thSx,
                        textAlign: c.type === 'days' ? 'right' : 'left',
                        ...(c.group === 'annual' ? { bgcolor: '#EEF6F8' } : null),
                        ...(i === 0 ? { ...stickyName, zIndex: 3, bgcolor: C.surface } : null),
                      }}>
                        <TableSortLabel active={orderBy === c.key} direction={orderBy === c.key ? order : 'asc'}
                          onClick={() => toggleSort(c.key)}>
                          <Box component="span" sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: c.type === 'days' ? 'flex-end' : 'flex-start' }}>
                            <span>{c.label}</span>
                            {c.sub && (
                              <Box component="span" sx={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500, color: C.ink400, fontSize: '0.62rem' }}>
                                {c.sub}
                              </Box>
                            )}
                          </Box>
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? null : (paged.length === 0 || noTypeChosen) ? (
                    <TableRow>
                      <TableCell colSpan={matrix.columns.length} align="center" sx={{ py: 6 }}>
                        <Typography variant="body2" color="text.disabled">
                          {noTypeChosen
                            ? 'Zgjidhni të paktën një lloj pushimi te "Llojet e pushimit".'
                            : search || departmentId || workerId ? 'Asnjë punonjës nuk përputhet me filtrat' : 'Nuk ka të dhëna për këtë vit'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : paged.map((r, ri) => (
                    <TableRow key={r.workerId} sx={{ bgcolor: ri % 2 ? '#FCFDFE' : C.white, '&:hover': { bgcolor: '#EEF2F7' } }}>
                      {matrix.columns.map((c, i) => {
                        const v = r[c.key];
                        const pend = c.typeId ? r[`${c.key}Pending`] : 0;
                        return (
                          <TableCell key={c.key} sx={{
                            ...tdSx,
                            textAlign: c.type === 'days' ? 'right' : 'left',
                            fontVariantNumeric: c.type === 'days' ? 'tabular-nums' : undefined,
                            fontWeight: c.emphasis ? 800 : (i === 0 ? 600 : undefined),
                            color: c.key === 'annualRemaining' ? (Number(v) < 0 ? C.bad : C.good) : (i === 0 ? C.ink : undefined),
                            ...(i === 0 ? stickyName : null),
                          }}>
                            {c.type === 'days' ? (v ? fmtDays(v) : '—') : (v || '—')}
                            {pend > 0 && (
                              <Tooltip title={`${fmtDays(pend)} ditë në pritje`}>
                                <Box component="span" sx={{ ml: 0.5, color: C.warn, fontSize: '0.7rem', fontWeight: 700 }}>+{fmtDays(pend)}</Box>
                              </Tooltip>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
                {!loading && paged.length > 0 && !noTypeChosen && (
                  <TableBody>
                    <TableRow sx={{
                      position: 'sticky', bottom: 0, zIndex: 2, bgcolor: C.surface,
                      '& td': { borderTop: `1px solid ${C.line}`, borderBottom: 0, bgcolor: C.surface, fontWeight: 800, color: C.ink },
                    }}>
                      {matrix.columns.map((c, i) => (
                        <TableCell key={c.key} sx={{
                          ...tdSx, textAlign: c.type === 'days' ? 'right' : 'left', fontVariantNumeric: 'tabular-nums',
                          ...(i === 0 ? { ...stickyName, zIndex: 3, bgcolor: C.surface } : null),
                        }}>
                          {i === 0 ? `Totali (${matrix.totals.workers})` : c.type === 'days' ? fmtDays(matrix.totals[c.key]) : ''}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          )}

          {/* ── Table ── */}
          {!error && !isAll && (
            <TableContainer sx={{ maxHeight: 'calc(100vh - 330px)', minHeight: 240 }}>
              <Table size="small" stickyHeader sx={{ '& td, & th': { borderColor: C.hair } }}>
                <TableHead>
                  <TableRow>
                    {visibleColumns.map((c, i) => (
                      <TableCell key={c.id} sx={{
                        ...thSx,
                        textAlign: c.type === 'days' ? 'right' : 'left',
                        ...(i === 0 ? { ...stickyName, zIndex: 3, bgcolor: C.surface } : null),
                      }}>
                        <TableSortLabel
                          active={orderBy === c.id}
                          direction={orderBy === c.id ? order : 'asc'}
                          onClick={() => toggleSort(c.id)}>
                          {c.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? null : paged.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={visibleColumns.length} align="center" sx={{ py: 6 }}>
                        <Typography variant="body2" color="text.disabled">
                          {search || departmentId || workerId
                            ? 'Asnjë punonjës nuk përputhet me filtrat'
                            : 'Nuk ka të dhëna për këtë vit'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : paged.map((r, ri) => (
                    <TableRow
                      key={r.workerId}
                      sx={{
                        bgcolor: ri % 2 ? '#FCFDFE' : C.white,
                        '&:hover': { bgcolor: '#EEF2F7' },
                      }}
                    >
                      {visibleColumns.map((c, i) => (
                        <TableCell key={c.id} sx={{
                          ...tdSx,
                          textAlign: c.type === 'days' ? 'right' : 'left',
                          fontVariantNumeric: c.type === 'days' ? 'tabular-nums' : undefined,
                          fontWeight: c.emphasis ? 800 : undefined,
                          color: c.emphasis ? (Number(r.remainingDays) < 0 ? C.bad : C.good) : undefined,
                          ...(i === 0 ? stickyName : null),
                        }}>
                          {c.id === 'workerName' ? (
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                              <Box component="span" sx={{ fontWeight: 600, color: C.ink }}>{r.workerName || '—'}</Box>
                              {r.hasDiscrepancy && (
                                <Tooltip title={r.discrepancies.map((d) => `${d.field}: ruajtur ${d.stored} · nga kërkesat ${d.fromRequests}`).join(' · ')}>
                                  <WarningAmberIcon sx={{ fontSize: 14, color: '#D97706' }} />
                                </Tooltip>
                              )}
                              {r.isPartialYear && (
                                <Tooltip title={`Vit i pjesshëm — ${r.employedDaysInYear} ditë të punësuara`}>
                                  <Chip label="pro-rata" size="small" sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700, bgcolor: 'primary.soft', color: 'primary.onSoft' }} />
                                </Tooltip>
                              )}
                            </Stack>
                          ) : cellValue(r, c)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>

                {/* Footer totals — summed by the backend over the whole filtered
                    set, and pinned so they stay readable while the list scrolls. */}
                {!loading && totals && paged.length > 0 && (
                  <TableBody>
                    <TableRow sx={{
                      position: 'sticky', bottom: 0, zIndex: 2, bgcolor: C.surface,
                      '& td': { borderTop: `1px solid ${C.line}`, borderBottom: 0, bgcolor: C.surface, fontWeight: 800, color: C.ink },
                    }}>
                      {visibleColumns.map((c, i) => (
                        <TableCell key={c.id} sx={{
                          ...tdSx,
                          textAlign: c.type === 'days' ? 'right' : 'left',
                          fontVariantNumeric: 'tabular-nums',
                          // Explicit, not `inherit`: this cell is both sticky-left
                          // and inside a sticky-bottom row, so it must paint itself.
                          ...(i === 0 ? { ...stickyName, zIndex: 3, bgcolor: C.surface } : null),
                        }}>
                          {i === 0
                            ? `Totali (${totals.workers})`
                            : c.type === 'days' ? fmtDays(totals[c.id]) : ''}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          )}

          {!error && (
            <TablePagination
              component="div" count={listForPaging.length} page={page} rowsPerPage={rpp}
              rowsPerPageOptions={[10, 25, 50, 100]}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={(e) => { setRpp(+e.target.value); setPage(0); }}
              labelRowsPerPage="Rreshta:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} nga ${count}`}
              sx={{
                borderTop: `1px solid ${C.hair}`, minHeight: 40,
                '& .MuiTablePagination-toolbar': { minHeight: 40, px: 1.5 },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: '0.75rem', color: C.ink500,
                },
                '& .MuiTablePagination-select': { fontSize: '0.75rem' },
              }}
            />
          )}
        </CardContent>
      </Card>

      <SendReportEmailDialog
        open={emailOpen} onClose={() => setEmailOpen(false)}
        reportTitle={`Balanca e pushimeve — ${typeName}`}
        subtitle={`Viti ${year}`}
        columns={emailCols} rows={exportRows}
      />
    </ReportPage>
  );
}
